import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Box, Button, ContextualNotification, FieldLabel, Grid, InlineNotification, TextInput, Typography, Field, Modal } from '@blueprint/ui';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { validateCredentials, clearErrorMessage, clearSuccessMessage, clearTimeoutPopup } from '../../actions';
import Recaptcha from '../Recaptcha/Recaptcha';
import { InlineNotificationComponent } from '../Notification/InlineNotificationComponent';
import CaptchaErrorModal from '../CaptchaErrorModal/CaptchaErrorModal';
import { isEmailValid } from '../../functions/isEmailValid';
import CONSTANTS from '../../constants/constants';
import { setCaptchaHeader } from '../../actions/index';

const GUEST_REGISTRATION = process.env.REACT_APP_GUEST_REGISTRATION;
const PAYMENT_LIST = process.env.REACT_APP_PAYMENT_LIST;

const StyledLink = styled(Link)`
    color: #333333;
    text-decoration: underline;
    &:hover {
        cursor: pointer;
        color: #333333;
    }
`;

export const GuestLogin = () => {
    const { t } = useTranslation();
    const history = useHistory();
    const dispatch = useDispatch();

    const [showError, setShowError] = useState(false);
    const [loginError, setLoginError] = useState('');

    const [emailId, setEmailId] = useState('');
    const [registrationCode, setRegistrationCode] = useState('');
    const globalState = useSelector((state) => state);

    const [captchaErrorModal, setCaptchaErrorModal] = useState(false);

    var CAPTCHA_ENABLED;
    if (process.env.NODE_ENV === 'development') {
        CAPTCHA_ENABLED = CONSTANTS.CAPTCHA_ENABLED;
    } else {
        CAPTCHA_ENABLED = globalState.beneDetails.captchaConfig.recaptchaEnabled;
    }
    const [captcha, setCaptcha] = useState('');
    const [captchaValidationError, setCaptchaValidationError] = useState(false);

    const [errorFlags, setErrorFlags] = useState({
        emailId: false,
        registrationCode: false,
        captcha: false,
    });

    const [errorMessages, setErrorMessages] = useState({
        emailId: '',
        registrationCode: '',
        captcha: '',
    });

    useEffect(() => {
        return () => {
            dispatch(clearErrorMessage());
            dispatch(clearSuccessMessage());
        };
    }, []);

    useEffect(() => {
        console.log('Logging global state before attempt to push inside from Guest Tab', globalState);

        if (globalState.beneDetails.details.isLogged && globalState.beneDetails.registeredUser) {
            dispatch(clearErrorMessage());
            history.push(PAYMENT_LIST);
        }

        if (globalState.beneDetails.details.isLogged && !globalState.beneDetails.registeredUser) {
            dispatch(clearErrorMessage());
            dispatch(clearErrorMessage());
            history.push(GUEST_REGISTRATION);
        }

        if (globalState.beneDetails.details && globalState.beneDetails.details.error) {
            console.log('Logging Error Message from Login Screen', globalState.beneDetails.error.message);
            console.log('Logging Error Status from Login Screen', globalState.beneDetails.error.status);
        }

        if (globalState.beneDetails.error && globalState.beneDetails.error.status === 401 && globalState.beneDetails.error.message) {
            console.log('Setting Login error Message');
            setLoginError(globalState.beneDetails.error.message);
            setShowError(true);
            setCaptchaValidationError(true);
        }

        if (!globalState.beneDetails.captchaToken) {
            setCaptcha(null);
        }
    }, [globalState, history, dispatch]);

    useEffect(() => {
        console.log('Error flags on Guest Login are', errorFlags);
    }, [errorFlags]);

    useEffect(() => {
        console.log('Error messages on Guest Login are', errorMessages);
    }, [errorMessages]);

    const validateEmail = () => {
        if (emailId.trim() === '' || emailId === null) {
            setErrorFlags({ ...errorFlags, emailId: true });
            setErrorMessages({ ...errorMessages, emailId: 'Please enter a valid email address' });
        } else {
            if (isEmailValid(emailId)) {
                setErrorFlags({ ...errorFlags, emailId: false });
                setErrorMessages({ ...errorMessages, emailId: '' });
            } else {
                setErrorFlags({ ...errorFlags, emailId: true });
                setErrorMessages({ ...errorMessages, emailId: 'Please enter a valid email address' });
            }
        }
    };

    const validateRegistrationCode = () => {
        if (registrationCode.trim() === '' || registrationCode === null || registrationCode === '') {
            setErrorFlags({ ...errorFlags, registrationCode: true });
            setErrorMessages({ ...errorMessages, registrationCode: 'Please enter a valid registration code' });
        } else {
            setErrorFlags({ ...errorFlags, registrationCode: false });
            setErrorMessages({ ...errorMessages, registrationCode: '' });
        }
    };

    const handleFormSubmit = () => {
        let areDetailsValid = true;

        let errorFlags = {
            emailId: false,
            registrationCode: false,
            captcha: false,
        };

        let errorMessages = {
            emailId: '',
            registrationCode: '',
            captcha: '',
        };

        // Validate email
        if (emailId.trim() === '' || emailId === null) {
            errorFlags.emailId = true;
            errorMessages.emailId = 'Please enter a valid email address';
            areDetailsValid = false;
        } else {
            if (isEmailValid(emailId)) {
                errorFlags.emailId = false;
                errorMessages.emailId = '';
            } else {
                errorFlags.emailId = true;
                errorMessages.emailId = 'Please enter a valid email address';
                areDetailsValid = false;
            }
        }

        // Validate registration code
        if (registrationCode.trim() === '' || registrationCode === null) {
            errorFlags.registrationCode = true;
            errorMessages.registrationCode = 'Please enter a valid registration code';
            areDetailsValid = false;
        } else {
            errorFlags.registrationCode = false;
            errorMessages.registrationCode = '';
        }

        // Validate captcha
        if (CAPTCHA_ENABLED) {
            if (captcha) {
                errorFlags.captcha = false;
                errorMessages.captcha = '';
            } else {
                errorFlags.captcha = true;
                errorMessages.captcha = 'Please verify captcha';
                areDetailsValid = false;
            }
        }

        console.log('Setting error messages in state');
        setErrorMessages({
            emailId: errorMessages.emailId,
            registrationCode: errorMessages.registrationCode,
            captcha: errorMessages.captcha,
        });

        console.log('Setting error flags in state');
        setErrorFlags({
            emailId: errorFlags.emailId,
            registrationCode: errorFlags.registrationCode,
            captcha: errorFlags.captcha,
        });

        if (areDetailsValid) {
            console.log('Validations passed, Dispatching action');
            dispatch(validateCredentials(emailId, registrationCode, 'GUEST'));
        } else {
            console.log('Validations failed');
        }
    };

    const [gridHeight, setGridHeight] = useState();
    const topMarginGridRef = useRef(0);

    useEffect(() => {
        if (topMarginGridRef.current.clientHeight) {
            setGridHeight(topMarginGridRef.current.clientHeight);
        }
    }, []);
    const footerHeight = globalState.beneDetails.footerHeight;

    const [open, setOpen] = useState(false);
    const [modalText, setModalText] = useState('');

    if (globalState.beneDetails.showTimeoutPopup && !open) {
        setOpen(true);
        setModalText(t('loginPage.yourOnlineSessionHasBeen'));
    }

    useEffect(() => {
        const captchaContainer = document.getElementById('recaptcha');
        setTimeout(() => {
            if (captchaContainer) {
                if (!captchaContainer.hasChildNodes() && errorFlags.captcha) {
                    setCaptchaErrorModal(true);
                } else {
                    setCaptchaErrorModal(false);
                }
            }
        }, 200);
    }, [errorFlags, globalState.beneDetails.captchaConfig]);
    return (
        <>
            <Box ref={topMarginGridRef}>
                <Box style={{ paddingTop: '130px', paddingBottom: `${footerHeight + 20}px`, display: 'flex', justifyContent: 'center' }}>
                    <Box style={{ backgroundColor: 'white', padding: '20px 2.5vw 20px 2.5vw', maxWidth: '600px' }}>
                        <Grid xs={12} item alignItems='center' justify='space-between'>
                            <Typography variant='h1' className='page-title' tabIndex='-1'>
                                {t('loginPage.hsbc_bm')}
                            </Typography>
                        </Grid>
                        {captchaErrorModal ? (
                            <Grid item xs={12}>
                                <Box mb={2} marginTop={4}>
                                    <ContextualNotification
                                        id='contextual-error'
                                        title=''
                                        variant='error'
                                        iconAriaLabel='error'
                                        onClose={() => {
                                            setCaptchaErrorModal(false);
                                            setErrorFlags({ ...errorFlags, captcha: false });
                                            dispatch(clearErrorMessage());
                                        }}
                                        aria-label={t('loginPage.captcha_error')}
                                    >
                                        {t('loginPage.captcha_error')}
                                    </ContextualNotification>
                                </Box>
                            </Grid>
                        ) : null}
                        {showError ? (
                            <Grid item xs={12}>
                                <Box mb={2} marginTop={4}>
                                    <ContextualNotification
                                        id='contextual-error'
                                        title=''
                                        variant='error'
                                        iconAriaLabel='error'
                                        onClose={() => {
                                            setShowError(false);
                                            dispatch(clearErrorMessage());
                                        }}
                                        aria-label={loginError}
                                    >
                                        {loginError}
                                    </ContextualNotification>
                                </Box>
                            </Grid>
                        ) : null}
                        {globalState.beneDetails.successMessage.status ? (
                            <Grid item xs={12}>
                                <Box mb={2} marginTop={4} maxWidth='600px'>
                                    <ContextualNotification
                                        id='contextual-success'
                                        title=''
                                        variant='success'
                                        iconAriaLabel='success'
                                        onClose={() => {
                                            dispatch(clearSuccessMessage());
                                        }}
                                    >
                                        {globalState.beneDetails.successMessage.statusText}
                                    </ContextualNotification>
                                </Box>
                            </Grid>
                        ) : null}
                        <Grid xs={12} item>
                            <Box marginTop={['5px', '5px', '5px', '5px']}>
                                <StyledLink
                                    style={{ fontSize: '14px' }}
                                    onClick={() => {
                                        dispatch(clearErrorMessage());
                                        dispatch(clearSuccessMessage());
                                        history.push('/login');
                                    }}
                                >
                                    {t('loginPage.existing_user')}
                                </StyledLink>
                            </Box>
                        </Grid>
                        <Field name='email-id'>
                            <Grid xs={12} item>
                                <Box marginTop={['5px', '5px', '5px', '10px']}>
                                    <FieldLabel>{t('loginPage.email')}</FieldLabel>
                                </Box>
                            </Grid>
                            <Grid xs={12} item>
                                <TextInput
                                    aria-labelledby='email-id-label'
                                    aria-describedby='contextual-error email-inline-error'
                                    placeholder='Email'
                                    required
                                    onChange={(event) => {
                                        setEmailId(event.value);
                                    }}
                                    onBlur={() => {
                                        validateEmail();
                                    }}
                                />
                            </Grid>
                        </Field>
                        {errorFlags.emailId ? (
                            <Grid item xs={12}>
                                <InlineNotificationComponent id='email-inline-error' title='' content={errorMessages.emailId} />
                            </Grid>
                        ) : null}
                        <Field name='registration-code'>
                            <Grid xs={12} item>
                                <Box marginTop={['5px', '5px', '5px', '10px']}>
                                    <FieldLabel aria-label='Registration code'>{t('loginPage.registration_code')}</FieldLabel>
                                </Box>
                            </Grid>
                            <Grid xs={12} item>
                                <TextInput
                                    aria-labelledby='registration-code'
                                    aria-describedby='contextual-error registration-inline-error'
                                    type='password'
                                    placeholder={t('loginPage.registration_code')}
                                    onChange={(event) => {
                                        setRegistrationCode(event.value);
                                    }}
                                    onBlur={() => {
                                        validateRegistrationCode();
                                    }}
                                />
                            </Grid>
                        </Field>
                        {errorFlags.registrationCode ? (
                            <Grid item xs={12}>
                                <InlineNotificationComponent id='registration-inline-error' title='' content={errorMessages.registrationCode} />
                            </Grid>
                        ) : null}
                        {CAPTCHA_ENABLED ? (
                            <>
                                <Grid container>
                                    <Box marginTop='1em' id='captcha-container-box'>
                                        <Recaptcha
                                            onVerification={(token) => {
                                                // Set header setHeaders({ 'X-HSBC-ReCaptcha-Response': token });
                                                console.log('Got a new Captcha token', token);
                                                dispatch(setCaptchaHeader(token));
                                                setCaptcha(token);
                                                setCaptchaValidationError(false);
                                                setErrorFlags({ ...errorFlags, captcha: false });
                                            }}
                                            onExpiry={() => {
                                                // Happens when timed out
                                                setCaptcha(null);
                                            }}
                                            resetCaptcha={captchaValidationError} // Boolean variable that indicates if there was error in captcha
                                            action='onVerification'
                                        />
                                    </Box>
                                </Grid>
                                {errorFlags.captcha ? (
                                    <Grid container>
                                        <Box>
                                            <InlineNotificationComponent id='login-captcha-error' title='' content={errorMessages.captcha} />
                                        </Box>
                                    </Grid>
                                ) : null}
                            </>
                        ) : null}

                        <Grid xs={12} container direction='row-reverse'>
                            <Box marginTop='1em'>
                                <Button
                                    aria-label='Log on button'
                                    id='guest-log-on'
                                    fontSize='body1'
                                    variant='light.primary'
                                    onClick={() => {
                                        handleFormSubmit();
                                    }}
                                >
                                    {t('loginPage.signin')}
                                </Button>
                            </Box>
                        </Grid>
                    </Box>
                </Box>
            </Box>

            <Modal
                open={open}
                onHide={() => {
                    setOpen(false);
                    dispatch(clearTimeoutPopup());
                }}
                closeIcon={true}
                onClickCloseIcon={() => {
                    setOpen(false);
                    dispatch(clearTimeoutPopup());
                }}
                size='small'
                activeFocusTrap={true}
                aria-labelledby='title1'
                minHeight='230px'
            >
                <Box display='flex' justifyContent='center' width='100%' marginTop='10%'>
                    <Box>
                        <Typography variant='h4' id='paymentsList-typo-modal-text' fontWeight='bold'>
                            {modalText}
                        </Typography>
                    </Box>
                </Box>
                <Box
                    position='absolute'
                    bottom='0'
                    py='1em'
                    width='calc(100% - 80px)'
                    display='flex'
                    justifyContent='center'
                    borderTop='1px solid #333'
                >
                    <Button
                        variant='light.primary'
                        onClick={() => {
                            setOpen(false);
                            dispatch(clearTimeoutPopup());
                        }}
                    >
                        OK
                    </Button>
                </Box>
            </Modal>
        </>
    );
};
